import { useTranslate } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import { Flex, Table, Tag, theme } from 'antd';
import type { RevenuecatEventOut } from '@/api/generated/schemas';
import { EmbeddedTableSearch } from '@/components/common/EmbeddedTableSearch';
import { ShowLink } from '@/components/common/ShowLink';
import { getSortOrder } from '@/utils/sorters';

interface CustomerEventsTableProps {
  accountId: number;
}

const mono = (value: string | null) =>
  value ? <span style={{ fontFamily: 'monospace' }}>{value}</span> : '-';
const dateOrDash = (value: string | null) =>
  value ? new Date(value).toLocaleString() : '-';

export const CustomerEventsTable = ({
  accountId,
}: CustomerEventsTableProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { tableProps, sorters, setFilters } = useTable<RevenuecatEventOut>({
    resource: 'monetization.revenuecat_events',
    filters: {
      permanent: [{ field: 'account_id', operator: 'eq', value: accountId }],
    },
    sorters: { initial: [{ field: 'id', order: 'desc' }] },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });
  const f = (key: string) => t(`monetization.revenuecat_events.fields.${key}`);

  return (
    <Flex vertical gap={token.marginSM}>
      <EmbeddedTableSearch
        setFilters={setFilters}
        placeholder={t('monetization.revenuecat_events.search.placeholder')}
      />
      <Table<RevenuecatEventOut>
        {...tableProps}
        rowKey="id"
        size="small"
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: t('messages.no_data') }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowLink resource="monetization.revenuecat_events" id={value} />
          )}
        />
        <Table.Column
          dataIndex="event_id"
          title={f('event_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'event_id')}
          render={mono}
        />
        <Table.Column
          dataIndex="event_type"
          title={f('event_type')}
          sorter
          sortOrder={getSortOrder(sorters, 'event_type')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
        />
        <Table.Column
          dataIndex="environment"
          title={f('environment')}
          sorter
          sortOrder={getSortOrder(sorters, 'environment')}
          render={(value: string | null) =>
            value ? (
              <Tag color={value === 'PRODUCTION' ? 'success' : 'warning'}>
                {value}
              </Tag>
            ) : (
              '-'
            )
          }
        />
        <Table.Column
          dataIndex="store"
          title={f('store')}
          sorter
          sortOrder={getSortOrder(sorters, 'store')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
        />
        <Table.Column
          dataIndex="product_id"
          title={f('product_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'product_id')}
          render={(value: string | null) => value ?? '-'}
        />
        <Table.Column
          dataIndex="transaction_id"
          title={f('transaction_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'transaction_id')}
          render={mono}
        />
        <Table.Column
          dataIndex="event_timestamp"
          title={f('event_timestamp')}
          sorter
          sortOrder={getSortOrder(sorters, 'event_timestamp')}
          render={dateOrDash}
        />
        <Table.Column
          dataIndex="created_at"
          title={f('created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          render={dateOrDash}
        />
      </Table>
    </Flex>
  );
};
